import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  UserGroupIcon,
  EyeIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  FunnelIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';

interface AnalyticsSession {
  _id: string;
  sessionId: string;
  ipAddress: string;
  userAgent: string;
  userId?: {
    _id: string;
    email: string;
    name?: string;
    company?: string;
  };
  startTime: Date;
  endTime?: Date;
  totalDuration: number;
  pageViews: Array<{
    path: string;
    title: string;
    timestamp: Date;
    duration: number;
  }>;
  entryPage: string;
  exitPage: string;
  pageCount: number;
  deviceType: string;
  browser: string;
  os: string;
  enriched: boolean;
  converted: boolean;
  actions: Array<{
    type: string;
    timestamp: Date;
    details: any;
  }>;
}

interface AnalyticsOverview {
  totalSessions: number;
  uniqueVisitors: number;
  convertedSessions: number;
  enrichedSessions: number;
  pageViews: number;
  avgSessionDuration: number;
  conversionRate: string;
  bounceRate: number;
}

const WebAnalytics: React.FC = () => {
  const [sessions, setSessions] = useState<AnalyticsSession[]>([]);
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [topPages, setTopPages] = useState<any[]>([]);
  const [funnel, setFunnel] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<AnalyticsSession | null>(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    enriched: '',
    converted: '',
    deviceType: ''
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, filter]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch overview
      const overviewRes = await axios.get('/api/analytics/overview', {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: dateRange.start, endDate: dateRange.end }
      });
      
      setOverview(overviewRes.data.overview);
      setTopPages(overviewRes.data.topPages);
      setFunnel(overviewRes.data.funnel);

      // Fetch sessions
      const sessionsRes = await axios.get('/api/analytics/sessions', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end,
          enriched: filter.enriched,
          converted: filter.converted,
          limit: 100
        }
      });
      
      setSessions(sessionsRes.data.sessions);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const enrichSession = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/analytics/enrich/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data
      fetchAnalytics();
    } catch (error) {
      console.error('Error enriching session:', error);
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'mobile':
        return <DevicePhoneMobileIcon className="h-4 w-4" />;
      case 'tablet':
        return <DevicePhoneMobileIcon className="h-4 w-4" />;
      default:
        return <ComputerDesktopIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Web Analytics</h2>
        <div className="flex gap-4">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-3 py-2 border rounded-lg"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-3 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900">{overview.totalSessions}</p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unique Visitors</p>
                <p className="text-2xl font-bold text-gray-900">{overview.uniqueVisitors}</p>
              </div>
              <UserGroupIcon className="h-8 w-8 text-green-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{overview.conversionRate}%</p>
              </div>
              <ArrowTrendingUpIcon className="h-8 w-8 text-purple-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg. Session Duration</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatDuration(overview.avgSessionDuration)}
                </p>
              </div>
              <ClockIcon className="h-8 w-8 text-orange-500" />
            </div>
          </motion.div>
        </div>
      )}

      {/* Conversion Funnel */}
      {funnel && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FunnelIcon className="h-5 w-5" />
            Conversion Funnel
          </h3>
          <div className="space-y-4">
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Eligibility Checks</span>
                <span className="text-sm text-gray-600">{funnel.eligibilityChecks}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div 
                  className="bg-blue-600 h-8 rounded-full flex items-center justify-center text-white text-sm"
                  style={{ width: '100%' }}
                >
                  100%
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Deal Selections</span>
                <span className="text-sm text-gray-600">{funnel.dealSelections}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div 
                  className="bg-blue-600 h-8 rounded-full flex items-center justify-center text-white text-sm"
                  style={{ 
                    width: `${funnel.eligibilityChecks > 0 ? (funnel.dealSelections / funnel.eligibilityChecks * 100) : 0}%` 
                  }}
                >
                  {funnel.eligibilityChecks > 0 ? Math.round(funnel.dealSelections / funnel.eligibilityChecks * 100) : 0}%
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Questionnaires</span>
                <span className="text-sm text-gray-600">{funnel.questionnaires}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div 
                  className="bg-blue-600 h-8 rounded-full flex items-center justify-center text-white text-sm"
                  style={{ 
                    width: `${funnel.eligibilityChecks > 0 ? (funnel.questionnaires / funnel.eligibilityChecks * 100) : 0}%` 
                  }}
                >
                  {funnel.eligibilityChecks > 0 ? Math.round(funnel.questionnaires / funnel.eligibilityChecks * 100) : 0}%
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Term Sheets</span>
                <span className="text-sm text-gray-600">{funnel.termSheets}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-8">
                <div 
                  className="bg-green-600 h-8 rounded-full flex items-center justify-center text-white text-sm"
                  style={{ 
                    width: `${funnel.eligibilityChecks > 0 ? (funnel.termSheets / funnel.eligibilityChecks * 100) : 0}%` 
                  }}
                >
                  {funnel.eligibilityChecks > 0 ? Math.round(funnel.termSheets / funnel.eligibilityChecks * 100) : 0}%
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Sessions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow"
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Recent Sessions</h3>
            <div className="flex gap-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search IP or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg"
                />
              </div>
              <select
                value={filter.enriched}
                onChange={(e) => setFilter({ ...filter, enriched: e.target.value })}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="">All Sessions</option>
                <option value="true">Enriched Only</option>
                <option value="false">Not Enriched</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visitor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Journey
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions
                .filter(session => 
                  !searchTerm || 
                  session.ipAddress.includes(searchTerm) ||
                  session.userId?.email?.includes(searchTerm)
                )
                .map((session) => (
                <tr key={session._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {session.ipAddress}
                      </div>
                      {session.userId && (
                        <div className="text-sm text-gray-500">
                          {session.userId.email}
                        </div>
                      )}
                      <div className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(session.startTime))} ago
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Entry:</span>
                        <span className="font-medium">{session.entryPage}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <EyeIcon className="h-3 w-3" />
                        {session.pageCount} pages
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getDeviceIcon(session.deviceType)}
                      <div className="text-sm">
                        <div>{session.browser}</div>
                        <div className="text-xs text-gray-500">{session.os}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDuration(session.totalDuration)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      {session.enriched && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Enriched
                        </span>
                      )}
                      {session.converted && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          Converted
                        </span>
                      )}
                      {!session.enriched && !session.converted && (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                          Visitor
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedSession(session)}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        View Details
                      </button>
                      {!session.enriched && (
                        <button
                          onClick={() => enrichSession(session.sessionId)}
                          className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center gap-1"
                        >
                          <SparklesIcon className="h-4 w-4" />
                          Enrich
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Session Details Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Session Details</h3>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-medium mb-2">Session Information</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">IP Address:</dt>
                      <dd className="font-medium">{selectedSession.ipAddress}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Start Time:</dt>
                      <dd className="font-medium">
                        {format(new Date(selectedSession.startTime), 'PPpp')}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Duration:</dt>
                      <dd className="font-medium">{formatDuration(selectedSession.totalDuration)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Pages Viewed:</dt>
                      <dd className="font-medium">{selectedSession.pageCount}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Device Information</h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Device:</dt>
                      <dd className="font-medium capitalize">{selectedSession.deviceType}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Browser:</dt>
                      <dd className="font-medium">{selectedSession.browser}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-gray-500">OS:</dt>
                      <dd className="font-medium">{selectedSession.os}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Page Journey</h4>
                <div className="space-y-2">
                  {selectedSession.pageViews.map((pageView, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{pageView.path}</div>
                        <div className="text-sm text-gray-500">
                          {format(new Date(pageView.timestamp), 'p')} • {formatDuration(pageView.duration)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedSession.actions.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium mb-2">Actions Taken</h4>
                  <div className="space-y-2">
                    {selectedSession.actions.map((action, index) => (
                      <div key={index} className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
                        <div className="font-medium text-blue-700">{action.type}</div>
                        <div className="text-sm text-blue-600">
                          {format(new Date(action.timestamp), 'p')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebAnalytics; 